import { requireService } from '@profgeosoft/di-ez';
import { BackLight, Search } from '@repo/go-icons';
import { Button, Input, Loader, Scrollbar } from '@repo/go-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { wrap } from 'src/packages/mobx-di/wrap';
import { hasValue } from 'src/packages/utils/has-value';

import type { TModelOption } from './directory-model-service';
import type { TModelsGroupKey } from './models-list-group/models-list-group';
import type { ChangeEvent } from 'react';

import { ModelsListGroup } from './models-list-group/models-list-group';

import styles from './models-list.module.scss';

export const MIN_SIZE = { width: 220 };

export const ModelsList = wrap(function ModelsList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { modelId } = useParams();
  const directoriesList = requireService('directoriesListService');
  const directoryPage = requireService('directoryPage');
  const ref = useRef<HTMLDivElement>(null);

  const { directoryModelService, isEditing } = directoryPage;
  const view = directoryModelService?.view;

  const headingText = view ? t(`labels:${view.mainDisplayAttr}.label`) : '';

  const [maxSize, setMaxSize] = useState({ width: document.body.clientWidth });
  const [isResizing, setIsResizing] = useState(false);
  const [size, setSize] = useState({ width: MIN_SIZE.width });
  const [searchValue, setSearchValue] = useState('');
  const [collapseControl, setCollapseControl] = useState<Record<TModelsGroupKey, boolean>>({
    active: true,
    archived: true,
  });

  const [startSize, setStartSize] = useState<{ width: number } | null>(null);
  const [startPosition, setStartPosition] = useState<{ x: number } | null>(null);

  const { activeModels, archivedModels } = useMemo(() => {
    const models = directoryModelService?.modelsList ?? [];
    const normalizedSearch = searchValue.trim().toLowerCase();
    const activeModels: TModelOption[] = [];
    const archivedModels: TModelOption[] = [];

    for (const model of models) {
      if (normalizedSearch && !model.label.toLowerCase().includes(normalizedSearch)) {
        continue;
      }

      if (model.status === 'ACTIVE') {
        activeModels.push(model);
      } else if (model.status === 'DELETED') {
        archivedModels.push(model);
      }
    }

    return { activeModels, archivedModels };
  }, [directoryModelService?.modelsList, searchValue]);

  const onBack = () => {
    if (!directoriesList.currentDirectory) {
      navigate('/');
      return;
    }

    navigate(`/${directoriesList.currentDirectory}`);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setSearchValue(value);

    if (value.trim()) {
      setCollapseControl({ active: true, archived: true });
    }
  };

  const handleResize = useCallback(
    (event: PointerEvent & { layerX: number }) => {
      if (!startSize) {
        return;
      }

      const deltaX = event.clientX - (startPosition?.x ?? 0);

      const newWidth = Math.max(Math.min(startSize.width + deltaX, maxSize.width), MIN_SIZE.width);
      setSize(() => ({ width: newWidth }));
    },
    [startPosition, startSize, maxSize],
  );

  useEffect(() => {
    if (isResizing) {
      const clb = (e: PointerEvent) => handleResize(e as PointerEvent & { layerX: number });

      document.addEventListener('pointermove', clb);

      return () => {
        document.removeEventListener('pointermove', clb);
      };
    }

    return void 0;
  }, [handleResize, isResizing]);

  useEffect(() => {
    const abortController = new AbortController();

    document.addEventListener('pointerup', () => setIsResizing(false), { signal: abortController.signal });

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    const parentNode = ref.current?.parentElement;

    if (!parentNode) {
      return;
    }

    const resizeObserver = new ResizeObserver((elements) => {
      const elem = elements[0];
      setMaxSize({ width: elem.contentRect.width / 2 });
    });

    resizeObserver.observe(parentNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (maxSize.width < size.width && !isResizing) {
      setSize({ width: maxSize.width });
    }
  }, [maxSize, size.width, isResizing]);

  useEffect(() => {
    return directoryModelService?.init();
  }, [directoryModelService]);

  useEffect(() => {
    if (!directoryModelService || !directoryModelService.modelsList.length) {
      return;
    }

    const { modelsList } = directoryModelService;

    if (!modelId) {
      const defaultModelId = modelsList.find((model) => model.status === 'ACTIVE')?.value ?? modelsList[0]?.value;

      if (!hasValue(defaultModelId)) {
        return;
      }

      const isNewModelSet = directoryModelService.setCurrentModel(defaultModelId);

      if (isNewModelSet) {
        navigate(defaultModelId.toString());
      }
      return;
    }

    const modelIdAsNumber = Number(modelId);
    const isExisting = !!modelsList.find((model) => model.value === modelIdAsNumber);

    if (isExisting) {
      const isNewModelSet = directoryModelService.setCurrentModel(modelIdAsNumber);

      if (isNewModelSet) {
        navigate(modelIdAsNumber.toString());
      }
    } else {
      const isNewModelSet = directoryModelService.setCurrentModel(null);

      if (isNewModelSet) {
        navigate('./');
      }
    }
  }, [modelId, directoryModelService, directoryModelService?.modelsList, navigate]);

  return (
    <>
      <div
        className={styles.wrapper}
        ref={ref}
        style={{
          width: size.width,
          minWidth: size.width,
          maxWidth: maxSize.width,
        }}
      >
        <div
          className={styles.handler}
          onPointerDown={(event) => {
            setStartPosition({ x: event.clientX });
            setStartSize({ width: size.width });
            setIsResizing(true);
          }}
          onPointerUp={() => setIsResizing(false)}
        />
        {!directoryModelService || directoryModelService.isModelsLoading ? (
          <Loader />
        ) : (
          <>
            <h4 className={styles.heading}>{headingText}</h4>
            <div className={styles.listControls}>
              <Input
                placeholder={t('directory:modelsList.search')}
                value={searchValue}
                onChange={handleSearchChange}
                className={styles.searchInput}
                prefix={<Search />}
              />
            </div>
            <div className={styles.listWrapper}>
              <Scrollbar>
                <ModelsListGroup
                  groupKey="active"
                  title={t('directory:modelsList.active')}
                  models={activeModels}
                  isOpen={collapseControl.active}
                  onToggle={() => setCollapseControl((prev) => ({ ...prev, active: !prev.active }))}
                />
                <ModelsListGroup
                  groupKey="archived"
                  title={t('directory:modelsList.archived')}
                  models={archivedModels}
                  isOpen={collapseControl.archived}
                  onToggle={() => setCollapseControl((prev) => ({ ...prev, archived: !prev.archived }))}
                />
              </Scrollbar>
            </div>
          </>
        )}
        <Button className={styles.backButton} icon={<BackLight />} disabled={isEditing} onClick={onBack}>
          {t('common:back')}
        </Button>
      </div>
      <div className={styles.tableWrapper}>
        <Outlet />
      </div>
      {isResizing && (
        <div
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 1,
            cursor: 'col-resize',
            userSelect: 'none',
          }}
        />
      )}
    </>
  );
});
